import { Close, Restore, RotateLeft, RotateRight, ZoomIn, ZoomOut } from '@mui/icons-material';
import React, { useState, useRef } from 'react';
import AvatarEditor, { AvatarEditorProps } from 'react-avatar-editor';
import ModalComponent from '~/Components/Modal/Modal.component';
import { BLACK, GREY } from '~/Constants/Colors.constants';
import { Button, IconButton, Typography, ErrorButton } from '~/UIKit';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '~/UIKit/Style';
import classNames from 'classnames';

const MAX_FILE_SIZE = 2000000; // The image is resized if the size exceeds the limit.

const useStyles = makeStyles((theme) => ({
  toolbar: {
    position: 'absolute',
    bottom: 195,
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: 'rgba(0,0,0,0.4)',
    borderRadius: 30,
  },
  imageEditorTitle: {
    fontSize: 28,
    marginBottom: 30,
    textAlign: 'center',
    margin: '80px 0 30px',
  },
  imageEditor: {
    display: 'block',
    margin: 'auto auto 30px',
    backgroundColor: BLACK,
  },
  closeButton: {
    position: 'absolute',
    top: 10,
    right: 10,
  },
  actionContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingBottom: 30,
    margin: 'auto',
    position: 'relative',
  },
  confirmButton: {
    width: 185,
    marginBottom: 10,
    height: 50,
    [theme.breakpoints.down('md')]: {
      width: 240,
    },
  },
  imageEditorZoomButton: {
    '&:disabled': {
      color: GREY,
    },
  },
}));

export interface ImageEditorModalProps {
  originalFile?: File;
  canvasStyle: Partial<AvatarEditorProps>;
  isLoading: boolean;
  imageSrc?: string | File;
  open: boolean;
  primaryButtonLabel?: string;
  onClose: () => void;
  onUpload: (imageDataUrl: string) => void;
  onDelete?: () => void;
  testPayload?: any;
}

export const ImageEditorModal = ({
  originalFile,
  canvasStyle,
  isLoading,
  imageSrc,
  open,
  primaryButtonLabel,
  onClose,
  onUpload,
  onDelete,
  testPayload = null,
}: ImageEditorModalProps) => {
  const { classes } = useStyles();
  // states
  const [rotate, setRotate] = useState(0);
  const [scale, setScale] = useState(1);
  const [localLoader, setLoader] = useState(false);

  const { t } = useTranslation();
  const imageEditorRef = useRef(testPayload);

  // controllers
  const handleResetImage = () => {
    setRotate(0);
    setScale(1);
  };
  const handleRotateRight = () => {
    setRotate(rotate + 45);
  };
  const handleRotateLeft = () => {
    setRotate(rotate - 45);
  };
  const handleZoomIn = () => {
    setScale(scale + 0.3);
  };
  const handleZoomOut = () => {
    if (scale === 1) return;
    setScale(scale - 0.3);
  };

  const handleResize = (imageDataUrl: string) =>
    new Promise<string>((resolve, reject) => {
      const img = new Image(); // Create an HTMLImageElement
      img.src = imageDataUrl;

      img.onload = () => {
        // Once the image is loaded, create an offscreen canvas
        const offScreenCanvas = document.createElement('canvas');
        const width = (canvasStyle?.width || 0) * scale;
        const height = (canvasStyle?.height || 0) * scale;
        offScreenCanvas.width = width;
        offScreenCanvas.height = height;

        const ctx = offScreenCanvas.getContext('2d');
        if (ctx) {
          // Draw the image on the canvas
          ctx.drawImage(img, 0, 0, width, height);
          // Return the resized image as a data URL
          resolve(offScreenCanvas.toDataURL('image/png', 0.9));
        } else {
          reject(new Error('Failed to get 2D context'));
        }
      };

      img.onerror = (error) => reject(error);
    });

  const getBase64ImageSize = (base64String: string): number => {
    // Strip the data URL prefix (e.g., 'data:image/png;base64,')
    const base64Data = base64String.split(',')[1];

    // Calculate the size of the base64 string in bytes
    const padding = base64Data.endsWith('==') ? 2 : base64Data.endsWith('=') ? 1 : 0;
    const sizeInBytes = (base64Data.length * 3) / 4 - padding;

    return sizeInBytes;
  };

  const handleOnUpload = async () => {
    setLoader(true);
    const imageCanvas = imageEditorRef?.current as any;
    try {
      const imageDataUrl =
        // The image is resized if the size exceeds the limit.
        originalFile && originalFile.size > MAX_FILE_SIZE
          ? imageCanvas?.getImageScaledToCanvas()?.toDataURL()
          : imageCanvas?.getImage()?.toDataURL();

      const base64ImageSize = imageDataUrl && getBase64ImageSize(imageDataUrl || '');

      const finalImage =
        base64ImageSize > MAX_FILE_SIZE ? await handleResize(imageDataUrl) : imageDataUrl;

      onUpload(finalImage || '');
    } catch (error) {
      console.error('Error resizing image:', error);
    }
    setLoader(false);
  };

  const handleDelete = () => {
    onDelete?.();
    onClose();
  };

  return (
    <ModalComponent onClose={onClose} open={open}>
      <div>
        <Button
          endIcon={<Close />}
          className={classes.closeButton}
          color="primary"
          onClick={onClose}
          variant="text"
        >
          {t('app.close')}
        </Button>
        <Typography variant="h4" className={classes.imageEditorTitle}>
          {t('app.uploadImage')}
        </Typography>

        <div className={classes.actionContainer}>
          <div className={classes.toolbar}>
            <IconButton color="secondary" onClick={handleResetImage} size="large">
              <Restore />
            </IconButton>
            <IconButton color="secondary" onClick={handleRotateRight} size="large">
              <RotateRight />
            </IconButton>
            <IconButton color="secondary" onClick={handleRotateLeft} size="large">
              <RotateLeft />
            </IconButton>
            <IconButton color="secondary" onClick={handleZoomIn} size="large">
              <ZoomIn />
            </IconButton>
            <IconButton
              className={classes.imageEditorZoomButton}
              disabled={scale === 1}
              color="secondary"
              onClick={handleZoomOut}
              size="large"
            >
              <ZoomOut />
            </IconButton>
          </div>
          <AvatarEditor
            {...canvasStyle}
            ref={imageEditorRef}
            image={imageSrc || ''}
            scale={scale}
            rotate={rotate}
            className={classes.imageEditor}
            style={{ width: '100%', height: 'auto' }}
          />
          <Button
            data-testid="upload-button"
            className={classes.confirmButton}
            color="primary"
            onClick={handleOnUpload}
            variant="contained"
            isLoading={isLoading || localLoader}
          >
            {primaryButtonLabel ? primaryButtonLabel : t('app.upload')}
          </Button>
          {onDelete ? (
            <ErrorButton
              className={classNames(classes.confirmButton)}
              onClick={handleDelete}
              variant="text"
            >
              {t('app.delete')}
            </ErrorButton>
          ) : (
            <Button
              className={classes.confirmButton}
              color="primary"
              onClick={onClose}
              variant="text"
            >
              {t('app.cancel')}
            </Button>
          )}
        </div>
      </div>
    </ModalComponent>
  );
};
